import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { useTranslation } from '../hooks';

const Portfolio = () => {
  const { t } = useTranslation();
  const [currentProject, setCurrentProject] = useState(0);
  const projects = t.portfolio.projects;

  const projectImages = [
    '/images/port1.jpg',
    '/images/port2.jpg',
    '/images/port4.jpg',
    '/images/port5.jpg',
    '/images/port6.jpg',
    '/images/port7.jpg'
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentProject((prev) => (prev + 1) % projects.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [projects.length]);

  const nextProject = () => {
    setCurrentProject((prev) => (prev + 1) % projects.length);
  };

  const prevProject = () => {
    setCurrentProject((prev) => (prev - 1 + projects.length) % projects.length);
  };

  return (
    <section id="portfolio" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <h2 className="text-4xl lg:text-5xl font-light mb-6 tracking-wide">{t.portfolio.title}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t.portfolio.subtitle}
          </p>
        </div>

        {/* Featured Project Carousel */}
        <div className="relative mb-20">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentProject * 100}%)` }}
            >
              {projects.map((project, index) => (
                <div key={project.id} className="w-full flex-shrink-0 relative">
                  <div className="aspect-[16/9] bg-gray-200">
                    <img
                      src={projectImages[index]}
                      alt={`${project.title} - M.A CONCEPT`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
                      <div className="max-w-3xl">
                        <div className="text-sm uppercase tracking-wider text-gray-300 mb-2">{project.category}</div>
                        <h3 className="text-4xl lg:text-5xl font-light mb-4 tracking-wide">{project.title}</h3>
                        <p className="text-xl mb-6 text-gray-300 leading-relaxed">{project.description}</p>
                        <div className="flex items-center space-x-8 text-sm uppercase tracking-wide">
                          <span>{project.year}</span>
                          <span className="flex items-center">
                            <MapPin size={16} className="mr-2" />
                            {project.location}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevProject}
            className="absolute left-8 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 backdrop-blur-md text-white p-4 transition-all"
            aria-label="Previous project"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            onClick={nextProject}
            className="absolute right-8 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 backdrop-blur-md text-white p-4 transition-all"
            aria-label="Next project"
          >
            <ChevronRight size={28} />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-3">
            {projects.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentProject(index)}
                className={`w-3 h-3 transition-all ${
                  currentProject === index ? 'bg-black' : 'bg-gray-300'
                }`}
                aria-label={`Go to project ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Project Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div key={project.id} className="group cursor-pointer">
              <div className="aspect-square overflow-hidden mb-6 bg-gray-100">
                <img
                  src={projectImages[index]}
                  alt={`${project.title} - M.A CONCEPT`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wider text-gray-500">{project.category}</div>
                <h3 className="text-xl font-medium tracking-wide">{project.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{project.description}</p>
                <div className="flex items-center justify-between text-xs uppercase tracking-wide text-gray-500 pt-2">
                  <span>{project.year}</span>
                  <span>{project.location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
